import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Mail, MapPin, Star, ShieldCheck, Clock, CheckCircle2 } from "lucide-react";
import { tenantHref } from "@/lib/tenantPath";
import { useT } from "@/lib/LanguageContext";
import ContactForm from "@/components/site/ContactForm";
import { TemplateShell } from "./shared/TemplateShell";
import { ClinicalHeader, ClinicalFooter, CLINICAL_MAIN_CLASS } from "./chrome/ClinicalChrome";
import { useSiteContent, useTestimonials, useBlogPosts, useServices } from "./shared/useSiteData";
import BlogGrid from "./shared/BlogGrid";
// Relative, not "@/": Hero.fillerText.test.js mounts this template with only a handful of
// "@/" modules virtual-mocked (no craco jest moduleNameMapper for "@/") — see Hero.jsx's
// own identical note.
import { hasText } from "../lib/siteContent";
import { formatQuote } from "../lib/testimonials";

/**
 * Clinical / Medical — confiance, structuré, Swiss high-contrast clair.
 * Double header (barre contact + nav) · hero structuré + image carrée + badges
 * de confiance · cartes blanches ombre douce · approche en étapes · témoignages
 * grille sur fond bleu glacé avec étoiles · footer 4 colonnes.
 */
/**
 * The shared ContactForm in this theme's idiom: the white rounded card with the soft blue
 * shadow that every service and testimonial uses, bordered rounded-md inputs, a semibold
 * accent-blue submit — the "clinical intake form" reading this theme is built on.
 */
const FORM_CLASSES = {
  grid: "grid sm:grid-cols-2 gap-5",
  fieldWide: "sm:col-span-2",
  label: "block text-xs font-semibold text-slate-500",
  input: "mt-1.5 w-full rounded-md border border-slate-200 px-3.5 py-2.5 text-slate-700 outline-none focus:border-[var(--tpl-accent)] transition-colors",
  textarea: "mt-1.5 w-full rounded-md border border-slate-200 px-3.5 py-2.5 text-slate-700 outline-none focus:border-[var(--tpl-accent)] transition-colors resize-none",
  hint: "mt-2 text-xs text-slate-500",
  actions: "mt-6 flex items-center gap-4 flex-wrap",
  submit: "inline-flex items-center gap-2 rounded-md px-7 py-3 font-semibold text-white bg-[var(--tpl-accent)] hover:brightness-110 transition disabled:opacity-60",
  success: "text-sm font-semibold text-emerald-600",
  error: "text-sm font-semibold text-red-600",
};

export default function ClinicalTemplate({ template, toggles = {} }) {
  const c = useSiteContent(toggles);
  const t = useT("templates");
  const testimonials = useTestimonials();
  const posts = useBlogPosts();
  const services = useServices();
  const show = (k) => toggles?.[k] !== false;

  // A stat reads as one badge, value then label ("2,200 h of training"): the theme used to
  // print the label alone, which silently dropped the figure a practitioner saved.
  const statText = (s) => [s?.v, s?.l].filter(Boolean).join(" ");
  const badges = [
    { icon: ShieldCheck, label: statText(c.hero.stats[0]) || t("clinical.badges.certified") },
    { icon: Clock, label: statText(c.hero.stats[1]) || t("clinical.badges.byAppointment") },
    { icon: CheckCircle2, label: statText(c.hero.stats[2]) || t("clinical.badges.provenApproach") },
  ];

  return (
    <TemplateShell template={template} branding={c.branding}>
      <main data-testid="tpl-clinical" className={CLINICAL_MAIN_CLASS}>
        {/* The double header (utility strip + sticky nav) is the theme's CHROME
            (chrome/ClinicalChrome.jsx), the same component the interior pages render
            through ThemedPageLayout — never an inline copy. */}
        <ClinicalHeader toggles={toggles} />

        {/* HERO */}
        <section id="top" className="py-16 md:py-24 px-6 md:px-10" data-testid="hero-section">
          <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
            <div>
              {/* BUG-210: no dictionary sample text backs this any more (lib/content.js) —
                  same guard idiom as Hero.jsx's hero-eyebrow. Guarding the whole element,
                  not just its text, is what keeps the coloured pill from rendering empty. */}
              {hasText(c.hero.eyebrow) && (
                <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded" style={{ background: "var(--tpl-secondary)", color: "var(--tpl-accent)" }} data-testid="hero-eyebrow">{c.hero.eyebrow}</span>
              )}
              <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="tpl-head text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] mt-5" style={{ color: "var(--tpl-primary)" }}>
                {c.hero.titleLines.map((l, i) => <span key={i} className="block">{l}</span>)}
              </motion.h1>
              {c.hero.sub && <p className="mt-6 text-lg text-slate-600 max-w-lg leading-relaxed">{c.hero.sub}</p>}
              <div className="mt-8 flex flex-wrap gap-4">
                <a href={tenantHref("/reserver")} data-testid="hero-primary-cta" className="inline-flex items-center gap-2 rounded-md px-7 py-3.5 font-semibold text-white hover:brightness-110 transition" style={{ background: "var(--tpl-accent)" }}>{c.hero.cta} <ArrowRight size={16} /></a>
                <a href="#services" className="inline-flex items-center gap-2 rounded-md px-7 py-3.5 font-semibold border-2" style={{ borderColor: "var(--tpl-primary)", color: "var(--tpl-primary)" }}>{c.hero.ctaSecondary}</a>
              </div>
              {/* BUG-196: bare `grid-cols-3` forced three badges into one row at every
                  width, including mobile. Single column below `sm`, 3-up from `sm:` up. */}
              <div data-testid="hero-stats" className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {badges.map((b, i) => { const Icon = b.icon; return (
                  <div key={i} className="flex items-center gap-2 rounded-lg border border-slate-200 p-3 text-xs font-semibold" style={{ color: "var(--tpl-primary)" }}>
                    <Icon size={18} style={{ color: "var(--tpl-accent)" }} /> {b.label}
                  </div>
                ); })}
              </div>
            </div>
            <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="relative">
              <img src={c.images.heroPortrait} alt="" className="w-full aspect-square object-cover rounded-2xl shadow-[0_30px_60px_-30px_rgba(52,152,219,0.4)]" data-testid="hero-portrait" />
              <div className="absolute -bottom-5 -left-5 bg-white rounded-xl shadow-lg border border-slate-100 p-4 flex items-center gap-3">
                <div className="flex">{[1, 2, 3, 4, 5].map((s) => <Star key={s} size={14} fill="#F59E0B" className="text-amber-500" />)}</div>
                <span className="text-sm font-semibold" style={{ color: "var(--tpl-primary)" }}>{c.brandName}</span>
              </div>
            </motion.div>
          </div>
        </section>

        {/* APPROACH — a bordered "protocol" panel between the hero and the services band:
            hairline rules top and bottom instead of a third tinted ground (this theme's
            rhythm alternates plain / --tpl-secondary), numbered square badges rather than
            the services icon, so the two grids stay legible as different things. */}
        {show("approach") && (
          <section id="approach" className="py-20 md:py-28 px-6 md:px-10 border-y border-slate-200" data-testid="approach-section">
            <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-10">
              <div className="lg:col-span-5">
                <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "var(--tpl-accent)" }}>{c.approach.eyebrow}</span>
                <h2 className="tpl-head text-3xl md:text-4xl font-extrabold mt-3 whitespace-pre-line" style={{ color: "var(--tpl-primary)" }}>{c.approach.title}</h2>
                <p className="mt-4 text-slate-600 leading-relaxed">{c.approach.lead}</p>
              </div>
              <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
                {c.approach.pillars.map((p, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }} data-testid={`approach-pillar-${i}`} className="rounded-xl border border-slate-200 p-6">
                    <div className="w-9 h-9 rounded-md flex items-center justify-center text-sm font-bold" style={{ background: "var(--tpl-secondary)", color: "var(--tpl-accent)" }}>{String(i + 1).padStart(2, "0")}</div>
                    <h3 className="tpl-head text-lg font-bold mt-4" style={{ color: "var(--tpl-primary)" }}>{p.title}</h3>
                    <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{p.body}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* SERVICES */}
        {show("services") && services.length > 0 && (
          <section id="services" className="py-20 md:py-28 px-6 md:px-10" style={{ background: "var(--tpl-secondary)" }} data-testid="services-section">
            <div className="max-w-7xl mx-auto">
              <div className="text-center max-w-2xl mx-auto">
                <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "var(--tpl-accent)" }}>{c.services.eyebrow}</span>
                <h2 className="tpl-head text-3xl md:text-5xl font-extrabold mt-3 whitespace-pre-line" style={{ color: "var(--tpl-primary)" }}>{c.services.title}</h2>
                <p className="mt-4 text-slate-600">{c.services.lead}</p>
              </div>
              <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {services.map((s, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }} data-testid={`service-card-${i}`} className="bg-white rounded-xl p-7 shadow-[0_10px_40px_-20px_rgba(44,62,80,0.25)] hover:-translate-y-1 transition-transform">
                    <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4" style={{ background: "var(--tpl-secondary)" }}><CheckCircle2 size={20} style={{ color: "var(--tpl-accent)" }} /></div>
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-400"><span>{s.tag}</span>·<span>{s.duration}</span></div>
                    <h3 className="tpl-head text-xl font-bold mt-2" style={{ color: "var(--tpl-primary)" }}>{s.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.body}</p>
                    <a href={tenantHref("/reserver")} data-testid={`service-book-cta-${i}`} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold hover:underline" style={{ color: "var(--tpl-accent)" }}>{c.nav.book} <ArrowRight size={14} /></a>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* STORY — étapes */}
        {show("story") && (
          <section id="story" className="py-20 md:py-28 px-6 md:px-10" data-testid="story-section">
            <div className="max-w-5xl mx-auto">
              <div className="text-center max-w-2xl mx-auto mb-14">
                <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "var(--tpl-accent)" }}>{c.story.eyebrow}</span>
                <h2 className="tpl-head text-3xl md:text-5xl font-extrabold mt-3 whitespace-pre-line" style={{ color: "var(--tpl-primary)" }}>{c.story.title}</h2>
                <p className="mt-4 text-slate-600">{c.story.lead}</p>
              </div>
              <div className="space-y-6">
                {c.story.chapters.map((ch, i) => (
                  <motion.div key={ch.n} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} data-testid={`story-chapter-${ch.n}`} className="flex gap-5 items-start rounded-xl border border-slate-200 p-6">
                    <div className="shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-white tpl-head text-xl font-bold" style={{ background: "var(--tpl-primary)" }}>{i + 1}</div>
                    <div><h3 className="tpl-head text-xl font-bold" style={{ color: "var(--tpl-primary)" }}>{ch.title}</h3><p className="mt-1.5 text-slate-600">{ch.body}</p></div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* TESTIMONIALS */}
        {show("testimonials") && testimonials.length > 0 && (
          <section id="testimonials" className="py-20 md:py-28 px-6 md:px-10" style={{ background: "var(--tpl-secondary)" }} data-testid="testimonials-section">
            <div className="max-w-6xl mx-auto">
              <h2 className="tpl-head text-3xl md:text-5xl font-extrabold text-center whitespace-pre-line" style={{ color: "var(--tpl-primary)" }}>{c.brandName}</h2>
              <div className="mt-12 grid md:grid-cols-3 gap-6">
                {testimonials.map((tm, i) => (
                  <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }} data-testid={`testimonial-${i}`} className="bg-white rounded-xl p-6 shadow-[0_10px_40px_-25px_rgba(44,62,80,0.3)]">
                    <div className="flex mb-3">{[1, 2, 3, 4, 5].map((s) => <Star key={s} size={14} fill="#F59E0B" className="text-amber-500" />)}</div>
                    <p className="text-slate-700 leading-relaxed text-sm">{formatQuote(tm.quote)}</p>
                    <div className="mt-4 font-semibold text-sm" style={{ color: "var(--tpl-primary)" }}>{tm.name}</div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* BLOG */}
        {show("blog") && posts.length > 0 && (
          <section id="blog" className="py-20 md:py-28 px-6 md:px-10" data-testid="blog-section">
            <BlogGrid
              posts={posts}
              gridClassName="max-w-7xl mx-auto gap-6"
              cardClassName="rounded-xl border border-slate-200 overflow-hidden group"
              renderCard={(p) => (
                <>
                  <img src={p.cover} alt="" className="w-full h-44 object-cover" />
                  <div className="p-6">
                    <div className="text-[11px] uppercase tracking-widest text-slate-400">{p.tag} · {p.date}</div>
                    <h3 className="tpl-head text-lg font-bold mt-2 group-hover:text-[var(--tpl-accent)] transition-colors" style={{ color: "var(--tpl-primary)" }}>{p.title}</h3>
                    <p className="mt-2 text-sm text-slate-600">{p.excerpt}</p>
                  </div>
                </>
              )}
            />
          </section>
        )}

        {/* CONTACT */}
        {show("contact") && (
          <section id="contact" className="py-20 md:py-28 px-6 md:px-10" style={{ background: "var(--tpl-primary)" }} data-testid="contact-section">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h2 className="tpl-head text-3xl md:text-5xl font-extrabold whitespace-pre-line">{c.contact.title}</h2>
              <p className="mt-4 text-white/80 max-w-xl mx-auto">{c.contact.lead}</p>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-white/90">
                <a href={`mailto:${c.contact.info.email}`} className="inline-flex items-center gap-2 hover:text-white" data-testid="contact-email-link"><Mail size={16} /> {c.contact.info.email}</a>
                <span className="inline-flex items-center gap-2"><MapPin size={16} /> {c.contact.info.place}</span>
              </div>
              <a href={tenantHref("/reserver")} className="mt-8 inline-flex items-center gap-2 rounded-md px-8 py-4 font-semibold text-white" style={{ background: "var(--tpl-accent)" }}>{c.nav.book} <ArrowRight size={16} /></a>
              <ContactForm
                className="mt-12 bg-white rounded-2xl p-7 md:p-9 text-left shadow-[0_30px_60px_-30px_rgba(0,0,0,0.5)]"
                classNames={FORM_CLASSES}
              />
            </div>
          </section>
        )}

        <ClinicalFooter toggles={toggles} />
      </main>
    </TemplateShell>
  );
}
